import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';

// Presentational, source-agnostic multi-select used to filter a listing table by category.
// It renders the shared "Filter by category" styling; each feature area supplies its own
// active-category source through a thin wrapper (see estimate-category-table-filter.js and
// resource-category-filter.js) so there is a single filter UI and no duplicated markup.
//   - `options`  : [{ value, label }] active categories to choose from
//   - `value`    : array of currently-selected category ids
//   - `onChange` : receives the next array of ids
const CategoryTableFilter = ({
  options = [],
  value = [],
  onChange,
  loading = false,
  size = 'small',
  placeholder = 'Filter by category',
}) => {
  const selectedOptions = useMemo(
    () => options.filter((option) => (value || []).includes(option.value)),
    [options, value]
  );

  return (
    <Autocomplete
      multiple
      size={size}
      fullWidth
      loading={loading}
      options={options}
      value={selectedOptions}
      onChange={(_, newValue) => onChange(newValue.map((option) => option.value))}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip size="small" label={option.label} {...getTagProps({ index })} key={option.value} />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} placeholder={selectedOptions.length ? '' : placeholder} />
      )}
      sx={{ width: 1 }}
    />
  );
};

CategoryTableFilter.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  placeholder: PropTypes.string,
};

export default CategoryTableFilter;
